import React, { useEffect, useState } from 'react';
import { TablePaginationConfig } from 'antd';
import { useSetRecoilState } from 'recoil';
import { useHistory, useLocation } from 'react-router';
import { useUpdateEffect, useWindowSize } from 'usehooks-ts';
import { ColumnType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';

import { defaultApi } from '../../../../apis';
import { getPageFromSearchParams, PAGE_INDEX_PARAM, useQuery } from '../../../../utils/urlUtil';
import { formatDateTimeIso, formatDateTimeShort } from '../../../../utils/stringUtil';
import { SearchThreadModel } from '../../../../../gamma-api';
import { alertsSelectorAdd } from '../../../../recoil';
import { LAYOUT_WIDTH_BREAKPOINTS } from '../../../../constants';
import IbTable from '../../../../components/common/IbTable';
import IbPagination from '../../../../components/common/IbPagination';
import ResizableDrawer from '../../../../components/common/ResizableDrawer';
import FilterableDebounceInput, {
  FilterableDebounceInputParam,
  IFilterableDebounceInputValue,
} from '../../../../components/common/FilterableDebounceInput';

import MessageListView from './MessageListView';

import './index.less';

interface IThreadsTabProps {
  projectId: string;
}

const THREADS_PAGE_SIZE = 30;
const THREAD_ID_PARAM = 'threadId';

const ThreadsTab: React.FC<IThreadsTabProps> = ({ projectId }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { push } = useHistory();
  const query = useQuery();
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const windowSize = useWindowSize();
  const isDesktop = windowSize.width > LAYOUT_WIDTH_BREAKPOINTS.small;

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: THREADS_PAGE_SIZE,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState<SearchThreadModel[]>([]);
  const [selectedThread, setSelectedThread] = useState<SearchThreadModel>();
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [inputValue, setInputValue] = useState<IFilterableDebounceInputValue>({ searchText: '' });

  const loadThreads = async () => {
    try {
      setLoading(true);
      const response = await defaultApi.searchAllThreadsSearchThreadsAllGet(
        [projectId],
        inputValue.searchText,
        formatDateTimeIso(inputValue.filter?.startDate, inputValue.filter?.startTime),
        formatDateTimeIso(inputValue.filter?.endDate, inputValue.filter?.endTime),
        (pagination.current || 1) - 1,
        THREADS_PAGE_SIZE,
        true
      );
      setThreads(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (e) {
      addAlert({ content: t('Project Page: Threads Tab: Failed to load threads'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadThread = async (threadId: string) => {
    try {
      const response = await defaultApi.getOneThreadSearchThreadsThreadIdGet(threadId);
      setSelectedThread(response.data);
    } catch (e) {
      addAlert({ content: t('Project Page: Threads Tab: Failed to load thread'), type: 'error' });
    }
  };

  useEffect(() => {
    const threadId = query.get(THREAD_ID_PARAM);
    if (!threadId) {
      setSelectedThread(undefined);
      setDrawerVisible(false);
    } else if (!selectedThread) {
      void loadThread(threadId);
      setDrawerVisible(true);
    }
  }, [location]);

  useEffect(() => {
    void loadThreads();
  }, [pagination.current, pagination.pageSize, inputValue.searchText, inputValue.filter]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [inputValue.searchText, inputValue.filter]);

  useUpdateEffect(() => {
    if (pagination.current) {
      query.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: query.toString() });
    }
  }, [pagination.current]);

  const renderDate = (item: SearchThreadModel) => {
    return formatDateTimeShort(item.created_on, i18n.language);
  };

  const columns: ColumnType<SearchThreadModel>[] = [
    {
      title: t('Project Page: Threads Tab: Table: Name'),
      render: (_, record) => {
        return record.name;
      },
      width: isDesktop ? '50%' : undefined,
      ellipsis: true,
    },
    {
      title: t('Project Page: Threads Tab: Table: Thread start date'),
      render: renderDate,
    },
  ];

  const getThreadRowKey = (thread: SearchThreadModel) => thread.thread_id;

  const onThreadRowClick = (thread: SearchThreadModel) => {
    setSelectedThread(thread);
    query.set(THREAD_ID_PARAM, thread.thread_id);
    push({ pathname: location.pathname, search: query.toString() });
    setDrawerVisible(true);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onDrawerClose = () => {
    setSelectedThread(undefined);
    query.delete(THREAD_ID_PARAM);
    push({ pathname: location.pathname, search: query.toString() });
    setDrawerVisible(false);
  };

  const drawerTitle = () => {
    if (!selectedThread) return;
    return <span>{`${t('Project Page: Threads Tab: Message List: Title')} ${renderDate(selectedThread)}`}</span>;
  };

  return (
    <div className="threads-tab">
      <FilterableDebounceInput
        params={[FilterableDebounceInputParam.DateTime]}
        value={inputValue}
        onChange={setInputValue}
      />
      <IbTable
        columns={columns}
        dataSource={threads}
        loading={loading}
        rowKey={getThreadRowKey}
        onRowClick={onThreadRowClick}
      />
      {!loading && (pagination.total ?? 0) > THREADS_PAGE_SIZE && (
        <div className="threads-tab__pagination-row">
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={THREADS_PAGE_SIZE}
            pageSize={THREADS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <ResizableDrawer
        resizable
        bodyStyle={{ padding: 0 }}
        title={drawerTitle()}
        visible={drawerVisible}
        width="75%"
        onClose={onDrawerClose}
      >
        <MessageListView thread={selectedThread} />
      </ResizableDrawer>
    </div>
  );
};

export default ThreadsTab;
